import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from 'lucide-react';

const PRESET_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '7', label: 'Last 7 Days' },
  { value: '15', label: 'Last 15 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '60', label: 'Last 60 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

export function DateFilter({ preset, onPresetChange, customFrom, customTo, onCustomFromChange, onCustomToChange, loading }) {
  return (
    <div className="flex items-center gap-3" data-testid="dashboard-date-filter">
      <Select value={preset} onValueChange={onPresetChange}>
        <SelectTrigger className="w-44 rounded-none border-zinc-300" data-testid="date-preset-select">
          <Calendar size={14} className="mr-2 text-zinc-400" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESET_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {preset === 'custom' && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="rounded-none border-zinc-300 gap-2 text-sm" data-testid="custom-date-trigger">
              <Calendar size={14} />
              {customFrom && customTo ? `${customFrom} — ${customTo}` : 'Pick dates'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4 rounded-none border-zinc-200" align="end">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">From</Label>
                <Input type="date" value={customFrom} onChange={(e) => onCustomFromChange(e.target.value)} className="rounded-none font-mono text-sm" data-testid="custom-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">To</Label>
                <Input type="date" value={customTo} onChange={(e) => onCustomToChange(e.target.value)} className="rounded-none font-mono text-sm" data-testid="custom-date-to" />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {loading && <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />}
    </div>
  );
}

export function StatCard({ icon: Icon, accent, value, label, testId }) {
  return (
    <div className="bg-white border border-zinc-200 p-5 transition-all duration-150 hover:border-zinc-400" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3"><div className={`w-8 h-8 ${accent} flex items-center justify-center`}><Icon size={16} className="text-white" /></div></div>
      <div className="font-mono text-2xl font-bold tracking-tight text-zinc-900">{value}</div>
      <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

export function CurrencyStatCard({ icon: Icon, accent, items, label, testId }) {
  return (
    <div className="bg-white border border-zinc-200 p-5 transition-all duration-150 hover:border-zinc-400" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3"><div className={`w-8 h-8 ${accent} flex items-center justify-center`}><Icon size={16} className="text-white" /></div></div>
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.currency || 'none'} className="flex items-baseline gap-1.5">
            <span className="font-mono text-lg font-bold tracking-tight text-zinc-900">{item.label}</span>
            {item.currency && <span className="text-xs text-zinc-400 font-mono">{item.currency}</span>}
          </div>
        ))}
      </div>
      <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
